import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ArrowLeft, Search } from "lucide-react";
import {
  CONTENT_KINDS,
  RELATION_LABELS,
  contentRequest,
  initialSelection,
  type ContentKind,
  type CommunityContentBlock,
  type ContentSelection,
} from "../../../lib/communityContent";
import { useDialogKeyboard } from "../../../lib/useDialogKeyboard";
import { ContentBlock } from "./ContentBlock";
type Item = { id: string; title: string; available: boolean; editUrl: string };
export function ContentPicker({
  onClose,
  onInsert,
  initial,
}: {
  onClose: () => void;
  onInsert: (block: CommunityContentBlock) => void;
  initial?: { kind: ContentKind; sourceId?: string };
}) {
  const [kind, setKind] = useState<ContentKind | "">(initial?.kind || ""),
    [items, setItems] = useState<Item[]>([]),
    [q, setQ] = useState(""),
    [search, setSearch] = useState(""),
    [page, setPage] = useState(0),
    [more, setMore] = useState(false),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(""),
    [block, setBlock] = useState<CommunityContentBlock | null>(null),
    [retry, setRetry] = useState(0);
  const version = useRef(0);
  const initialUsed = useRef(false);
  const closeDialog = useCallback(
    (value: boolean | ((previous: boolean) => boolean)) => {
      if (value === false) onClose();
    },
    [onClose],
  );
  useDialogKeyboard(true, closeDialog, "내 콘텐츠 넣기");
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
      version.current++;
    };
  }, []);
  useEffect(() => {
    if (!kind) return;
    const n = ++version.current;
    setLoading(true);
    setError("");
    if (page === 0) setItems([]);
    contentRequest<{ items: Item[]; hasMore: boolean }>("list", {
      kind,
      q: search,
      page,
    })
      .then((r) => {
        if (n !== version.current) return;
        setItems((old) => (page === 0 ? r.items : [...old, ...r.items]));
        setMore(r.hasMore);
      })
      .catch((e) => {
        if (n === version.current) setError(e.message);
      })
      .finally(() => {
        if (n === version.current) setLoading(false);
      });
  }, [kind, search, page, retry]);
  async function choose(sourceId: string, selectedKind = kind) {
    if (!selectedKind) return;
    const n = ++version.current;
    setLoading(true);
    setError("");
    try {
      const value = await contentRequest<CommunityContentBlock>(
        "preview",
        { kind: selectedKind, sourceId },
        "POST",
      );
      if (n !== version.current) return;
      const b = { ...value, id: crypto.randomUUID() };
      setBlock({ ...b, selection: initialSelection(b) });
    } catch (e) {
      if (n === version.current)
        setError(
          e instanceof Error ? e.message : "콘텐츠를 불러오지 못했습니다.",
        );
    } finally {
      if (n === version.current) setLoading(false);
    }
  }
  useEffect(() => {
    if (initial?.sourceId && !initialUsed.current) {
      initialUsed.current = true;
      void choose(initial.sourceId, initial.kind);
    }
    return () => {
      initialUsed.current = false;
    };
  }, []);
  function update(patch: Partial<ContentSelection>) {
    setBlock((b) =>
      b ? { ...b, selection: { ...b.selection, ...patch } } : b,
    );
  }
  function toggle(key: string) {
    const keys = block?.selection?.keys || [];
    update({
      keys: keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key],
      reviewed: false,
    });
  }
  const selectable =
    block?.kind === "wordcloud"
      ? block.data.words?.map((w) => ({
          key: w.key!,
          label: `${w.text} · ${w.count}회`,
        }))
      : block?.kind === "peerquiz"
        ? block.data.questions?.map((q) => ({
            key: q.key!,
            label: `${q.round ? `${q.round}회차 · ` : ""}${q.text}`,
          }))
        : block?.kind === "sociometry"
          ? block.data.nodes?.map((n) => ({ key: n.id, label: n.label }))
          : null;
  const valid = Boolean(
    block &&
    (block.mode === "reference" ||
      (block.kind === "prompt"
        ? block.selection?.prompt?.trim()
        : block.selection?.keys?.length)) &&
    (block.kind !== "sociometry" || block.selection?.reviewed),
  );
  return createPortal(
    <div className="fe-content-overlay">
      <section
        role="dialog"
        aria-modal="true"
        aria-label="내 콘텐츠 넣기"
        className="fe-content-picker"
      >
        <header>
          <div>
            <span className="fe-community-small-label">MY CONTENT</span>
            <h2>내 콘텐츠 넣기</h2>
          </div>
          <button type="button" aria-label="콘텐츠 선택 닫기" onClick={onClose}>
            <X />
          </button>
        </header>
        {(kind || block) && (
          <button
            type="button"
            className="fe-content-back"
            onClick={() => {
              version.current++;
              setLoading(false);
              setError("");
              if (block) {
                setBlock(null);
                setRetry((x) => x + 1);
              } else {
                setKind("");
                setItems([]);
                setPage(0);
                setSearch("");
                setQ("");
              }
            }}
          >
            <ArrowLeft size={16} />
            {block ? "다른 항목 선택" : "프로그램 선택"}
          </button>
        )}
        {!kind ? (
          <>
            <p>직접 만든 콘텐츠와 활동 결과를 글 안에 담아보세요.</p>
            <div className="fe-content-kind-grid">
              {CONTENT_KINDS.map((k) => (
                <button
                  type="button"
                  key={k.kind}
                  onClick={() => setKind(k.kind)}
                >
                  <strong>{k.label}</strong>
                  <span>{k.hint}</span>
                </button>
              ))}
            </div>
          </>
        ) : !block ? (
          <>
            <h3>
              {CONTENT_KINDS.find((k) => k.kind === kind)?.label} · 내 항목
            </h3>
            <form
              className="fe-content-search"
              onSubmit={(e) => {
                e.preventDefault();
                setPage(0);
                setSearch(q.trim());
                setRetry((v) => v + 1);
              }}
            >
              <input
                aria-label="내 콘텐츠 검색"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="제목으로 찾기"
              />
              <button type="submit" className="fe-community-button">
                <Search size={16} />
                검색
              </button>
            </form>
            <div className="fe-content-items">
              {items.map((item) => (
                <div key={item.id}>
                  <strong>{item.title}</strong>
                  {item.available ? (
                    <button
                      type="button"
                      className="fe-community-button"
                      disabled={loading}
                      onClick={() => choose(item.id)}
                    >
                      선택
                    </button>
                  ) : (
                    <a
                      href={item.editUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      공개 설정 확인 ↗
                    </a>
                  )}
                </div>
              ))}
            </div>
            {!loading && !error && !items.length && (
              <p className="fe-content-note">
                아직 선택할 내 콘텐츠가 없습니다. 프로그램에서 콘텐츠를 만든 뒤
                다시 불러와주세요.
              </p>
            )}
            {!loading && more && (
              <button
                className="fe-community-button"
                type="button"
                onClick={() => setPage((p) => p + 1)}
              >
                더 보기
              </button>
            )}
            <button
              type="button"
              className="fe-content-back"
              disabled={loading}
              onClick={() => {
                setPage(0);
                setRetry((r) => r + 1);
              }}
            >
              목록 새로고침
            </button>
          </>
        ) : (
          <>
            {block.mode === "snapshot" && (
              <div className="fe-content-review">
                <label>
                  게시용 제목
                  <input
                    value={block.selection?.title || ""}
                    maxLength={180}
                    onChange={(e) => update({ title: e.target.value })}
                  />
                </label>
                {block.kind === "prompt" && (
                  <label>
                    공유할 프롬프트 · 수정 가능
                    <textarea
                      rows={10}
                      value={block.selection?.prompt || ""}
                      onChange={(e) => update({ prompt: e.target.value })}
                    />
                    <span>
                      이름·연락처 등 공개하지 않을 내용은 지워주세요. 파일과
                      검토 결과는 포함하지 않습니다.
                    </span>
                  </label>
                )}
                {selectable && (
                  <>
                    {selectable.length === 0 && (
                      <p className="fe-content-note">
                        아직 공유할 결과가 없습니다. 활동에 응답이나 문제가 쌓인
                        뒤 다시 선택해주세요.
                      </p>
                    )}
                    <p>
                      {block.kind === "sociometry"
                        ? "공개할 참가자 노드"
                        : "공유할 내용"}
                      를 선택하세요.
                    </p>
                    <p className="fe-content-note">
                      이름·개인정보가 담긴 단어 또는 문제는 선택에서
                      제외해주세요.
                    </p>
                    <div className="fe-content-select-actions">
                      <button
                        type="button"
                        onClick={() =>
                          update({
                            keys: selectable.map((x) => x.key),
                            reviewed: false,
                          })
                        }
                      >
                        전체 선택
                      </button>
                      <button
                        type="button"
                        onClick={() => update({ keys: [], reviewed: false })}
                      >
                        전체 해제
                      </button>
                      <span>{block.selection?.keys?.length || 0}개 선택</span>
                    </div>
                    <div className="fe-content-select-list">
                      {selectable.map((item) => (
                        <label key={item.key}>
                          <input
                            type="checkbox"
                            checked={
                              block.selection?.keys?.includes(item.key) || false
                            }
                            onChange={() => toggle(item.key)}
                          />
                          <span>{item.label}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
                {block.kind === "sociometry" && (
                  <>
                    <fieldset>
                      <legend>공개할 관계 유형</legend>
                      <div className="fe-content-relation-options">
                        {Object.entries(RELATION_LABELS).map(([key, label]) => (
                          <label key={key}>
                            <input
                              type="checkbox"
                              checked={
                                block.selection?.relations?.includes(key) ||
                                false
                              }
                              onChange={() => {
                                const r = block.selection?.relations || [];
                                update({
                                  relations: r.includes(key)
                                    ? r.filter((x) => x !== key)
                                    : [...r, key],
                                  reviewed: false,
                                });
                              }}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    {!!block.data.mood?.length && (
                      <label>
                        <input
                          type="checkbox"
                          checked={block.selection?.includeMood || false}
                          onChange={(e) =>
                            update({
                              includeMood: e.target.checked,
                              reviewed: false,
                            })
                          }
                        />
                        기분 응답 분포 포함
                      </label>
                    )}
                    <p>{block.data.note}</p>
                  </>
                )}
              </div>
            )}
            <ContentBlock block={block} preview />
            {block.kind === "sociometry" && (
              <label className="fe-content-confirm">
                <input
                  type="checkbox"
                  checked={block.selection?.reviewed || false}
                  onChange={(e) => update({ reviewed: e.target.checked })}
                />
                위 관계도와 공개 내용을 확인했습니다.
              </label>
            )}
            <footer>
              <p>
                {block.mode === "reference"
                  ? "공개 원본이 바뀌면 함께 반영됩니다."
                  : "지금 검토한 결과를 공유본으로 보관합니다."}
              </p>
              <button
                type="button"
                className="fe-community-button"
                disabled={!valid || loading}
                onClick={() => {
                  if (block) onInsert(block);
                }}
              >
                이 콘텐츠 넣기
              </button>
            </footer>
          </>
        )}
        {loading && <p role="status">내 콘텐츠를 불러오고 있어요…</p>}
        {error && (
          <div role="alert">
            <p>{error}</p>
            <button
              type="button"
              className="fe-community-button"
              onClick={() => {
                if (initial?.sourceId && !block) void choose(initial.sourceId);
                else setRetry((v) => v + 1);
              }}
            >
              다시 시도
            </button>
          </div>
        )}
      </section>
    </div>,
    document.body,
  );
}
